import { useEffect, useRef, useState } from 'react';
import { registerSessionCache } from '../../lib/session-caches';
import { locationToQuery } from './lib/locationToQuery';
import { geocodeLocation, type GeoPos } from './lib/geocodeLocation';
import { LocationPreviewMap } from './LocationPreviewMap';

/**
 * 일정 상세의 «장소 지도» — 저장된 글자로 좌표를 다시 찾아 핀 하나를 보여 준다.
 *
 * ## 왜 다시 찾나
 *
 * 일정에는 좌표가 **저장되지 않는다** — `CalendarEvent` 에 `location String? @db.VarChar(200)`
 * 글자 하나뿐이고 좌표 칸은 0개다(2026-09-29 실측). 장소 고르개는 고를 때 좌표를 알지만
 * 화면 안에만 두고 글자만 올려보낸다. ⇒ 상세에서는 그 글자를 카카오에 다시 물어야 한다.
 *
 * ## 안 그리는 경우가 «먼저»다
 *
 * 사람이 손으로 적은 「3층 회의실」·「온라인」을 그대로 검색하면 **엉뚱한 곳에 핀이 꽂힌다** —
 * 지도가 없는 것보다 나쁘다. 그 판정은 `locationToQuery` 가 한다(제 시험으로 잠겨 있다).
 * 여기서는 그것이 null 이면 **아무것도 그리지 않는다** — 빈 지도도, 자리도 남기지 않는다.
 *
 * ## 높이 110px — ⭐ 지어낸 값이 아니다
 *
 * 일정을 **만들 때** 장소를 고르면 나오는 미리보기 지도가 이미 110px 이다
 * (`.calendar-location-picker-map`). 만들 때와 볼 때가 같은 크기라 눈에 익는다.
 * 그래서 `LocationPreviewMap` 을 그대로 쓰고 높이를 따로 정하지 않는다
 * (2026-09-29 사용자가 시안 「안 A」로 고른 값이다).
 *
 * ## 같은 장소를 두 번 묻지 않는다
 *
 * 상세를 열 때마다 검색이 나가면 같은 회의를 세 번 열면 세 번 나간다. 그래서 «검색어»를 열쇠로
 * 모듈 수준에 기억해 둔다. 못 찾은 것(null)도 기억한다 — 안 그러면 못 찾는 장소가 열 때마다
 * 헛되이 다시 나간다.
 * ⚠ 새로고침하면 비워진다. 그것으로 충분하다 — 장소 글자가 바뀌면 열쇠도 바뀌므로 낡지 않는다.
 */
const cache = new Map<string, GeoPos | null>();

/*
  ★ 로그아웃하면 비운다 (2026-08-31 규율 · `lib/session-caches.ts`).
  ⭐ 이 줄은 제가 잊고 있다가 **레포 가드가 잡았습니다** — `session-caches.test.ts` 가
    「파일 스코프 캐시를 가진 모듈은 모두 세션 종료를 듣는다」를 전수로 훑는다.
  ⚠ 담기는 것은 «공개 좌표»라 사람마다 다르지 않다 — 그래서 「세션 무관」 목록에 넣을 수도 있었다.
    그래도 등록하는 쪽을 골랐다: 잃는 것이 «다음 사람의 첫 검색 한 번»뿐이고,
    「이 캐시는 안전한가」를 다음 사람이 다시 따질 일이 없어진다.
*/
registerSessionCache(() => { cache.clear(); });

/** 시험 전용 — 통 사이에 기억이 새지 않게 비운다. */
export function _resetLocationMapCacheForTests(): void {
  cache.clear();
}

export function EventLocationMap({ location, onSettled }: {
  location: string | null | undefined;
  /** 찾기가 끝나면 알린다 — 지도를 그리면 true, 못 찾았거나 찾을 수 없는 글자면 false(2026-10-30 · 옵셔널). */
  onSettled?: (found: boolean) => void;
}) {
  const query = locationToQuery(location);
  const [pos, setPos] = useState<GeoPos | null>(() => (query ? cache.get(query) ?? null : null));
  const settledRef = useRef(onSettled);
  settledRef.current = onSettled;
  const settle = (found: boolean) => { settledRef.current?.(found); };

  useEffect(() => {
    if (!query) { setPos(null); settle(false); return; }
    if (cache.has(query)) { const hit = cache.get(query) ?? null; setPos(hit); settle(hit !== null); return; }
    let cancelled = false;
    void geocodeLocation(query)
      .then((found) => {
        cache.set(query, found);
        if (!cancelled) { setPos(found); settle(found !== null); }
      })
      .catch(() => {
        // 못 찾은 것으로 다룬다 — 지도를 안 그릴 뿐, 장소 «글자»는 그대로 보인다.
        cache.set(query, null);
        if (!cancelled) { setPos(null); settle(false); }
      });
    return () => { cancelled = true; };
  }, [query]);

  if (!query || !pos) return null;
  return <LocationPreviewMap pos={pos} />;
}
